/**
 * EXPORT DATA MODAL - SELECTION MODE CONFIGURATION
 *
 * This modal supports two modes for selecting Product/Environment:
 *
 * 1. SELECT BOTH (Currently Commented Out):
 *    - Users can select both Product and Environment simultaneously
 *    - Both selections will be exported together in a single CSV
 *
 *    TO ENABLE "SELECT BOTH" MODE:
 *    - UNCOMMENT: Lines 130-142 (handleProductToggle and handleEnvironmentToggle that allow both)
 *    - COMMENT: Lines 110-128 (handleProductToggle and handleEnvironmentToggle that enforce single selection)
 *    - UNCOMMENT: Lines 268-327 (Export logic that handles both Product and Environment)
 *    - COMMENT: Lines 215-267 (Export logic that uses if-else for single category)
 *    - UNCOMMENT: Lines 452-456 (Filename generation using categories.join("_"))
 *    - COMMENT: Lines 447-450 (Filename generation using single category variable)
 *
 * 2. SELECT ONLY ONE (Current Active Mode):
 *    - Users can select either Product OR Environment (mutually exclusive)
 *    - Only the selected category will be exported
 *
 *    TO ENABLE "SELECT ONLY ONE" MODE:
 *    - UNCOMMENT: Lines 110-128 (handleProductToggle and handleEnvironmentToggle that enforce single selection)
 *    - COMMENT: Lines 130-142 (handleProductToggle and handleEnvironmentToggle that allow both)
 *    - UNCOMMENT: Lines 215-267 (Export logic that uses if-else for single category)
 *    - COMMENT: Lines 268-327 (Export logic that handles both Product and Environment)
 *    - UNCOMMENT: Lines 447-450 (Filename generation using single category variable)
 *    - COMMENT: Lines 452-456 (Filename generation using categories.join("_"))
 */

import React, { useState, useEffect, useRef } from "react";

import { InfoCircleOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Modal } from "antd";
import moment from "moment";
import { CSVLink } from "react-csv";
import { toast } from "react-toastify";

import { EXPORT_SPEC_FLAG_LABELS } from "../../Common/constants/SpecFlagConstants";
import DatePicker from "../../Common/DatePicker/DatePicker";
import StyledTooltip from "../../Common/UIComponents/StyledTooltip";
import { useAccessStatus } from "../../grace-period/hooks/useAccessStatus";
import SubmissionFilterButton from "../MainPage/Common/SubmissionFilterButton";

import {
  getUserCredentials,
} from "../../../utils/common";

import { exportSampleSubmissions } from "../../../actions/sampleSubmission";

import { ReactComponent as CalendarIcon } from "../../../assets/images/feature-icons/CalendarEnv.svg";
import { ReactComponent as EnvironmentIcon } from "../../../assets/images/feature-icons/EnvironmentIconFilled.svg";
import { ReactComponent as ProductIcon } from "../../../assets/images/feature-icons/ProductIconFilled.svg";

import "./ExportDataModal.css";

const getDefaultDateRange = () => {
  const endDate = moment().format("MMM DD, YYYY");
  const startDate = moment().subtract(3, "months").format("MMM DD, YYYY");
  return `${startDate} - ${endDate}`;
};

/**
 * Formats a test cell for CSV export: result with optional mapped spec label.
 * - Result + mapped Spec_flag → "result (Label)"
 * - Result only → "result"
 * - Otherwise → ""
 * @param {*} result
 * @param {string|number|null|undefined} specFlag
 * @returns {string}
 */
const formatExportTestCellValue = (result, specFlag) => {
  const resultText = result !== undefined && result !== null ? String(result) : "";
  if (resultText.trim() === "") {
    return "";
  }
  const specLabel = EXPORT_SPEC_FLAG_LABELS[specFlag] ?? "";
  return specLabel ? `${resultText} (${specLabel})` : resultText;
};

export default function ExportDataModal({
  onCancel, heading = "Export Submission Data",
}) {
  const [isProductEnabled, setIsProductEnabled] = useState(false);
  const [isEnvironmentEnabled, setIsEnvironmentEnabled] = useState(false);
  const [productSelected, setProductSelected] = useState(false);
  const [environmentSelected, setEnvironmentSelected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [dateSelected, setDateSelected] = useState("");
  const [prevDateSelected, setPrevDateSelected] = useState("");
  const [exportData, setExportData] = useState([]);
  const [exportHeaders, setExportHeaders] = useState([]);
  const [csvFilename, setCsvFilename] = useState("Submissions_Export.csv");
  const csvLinkRef = useRef(null);
  const { areResultsLocked } = useAccessStatus();

  // Check if only one option is available (used to hide close icon and prevent deselection)
  const hasOnlyOneOption = (isProductEnabled && !isEnvironmentEnabled) || (!isProductEnabled && isEnvironmentEnabled);

  // Set default date range and enable both Product and Environment
  useEffect(() => {
    // Enable both Product and Environment by default
    setIsProductEnabled(true);
    setIsEnvironmentEnabled(true);

    // Set default selection - select Product by default
    setProductSelected(true);
    setEnvironmentSelected(false);

    // Set default date range to last 3 months from today
    const defaultDateRange = getDefaultDateRange();
    setDateSelected(defaultDateRange);
    setPrevDateSelected(defaultDateRange);
  }, []);

  const handleDateChange = (newValue) => {
    if (!newValue) {
      const defaultDateRange = getDefaultDateRange();
      setDateSelected(defaultDateRange);
      setPrevDateSelected(defaultDateRange);
      return;
    }
    setDateSelected(newValue);
  };

  // Toggle product selection - only one can be selected at a time
  // If only one option is available, prevent deselection
  const handleProductToggle = () => {
    if (!isProductEnabled) return;
    // If only product is available, don't allow deselection
    if (hasOnlyOneOption && productSelected) return;
    setProductSelected(true);
    setEnvironmentSelected(false); // Deselect environment when product is selected
  };

  // Toggle environment selection - only one can be selected at a time
  // If only one option is available, prevent deselection
  const handleEnvironmentToggle = () => {
    if (!isEnvironmentEnabled) return;
    // If only environment is available, don't allow deselection
    if (hasOnlyOneOption && environmentSelected) return;
    setEnvironmentSelected(true);
    setProductSelected(false); // Deselect product when environment is selected
  };

  // // Toggle product selection - allows selecting both (COMMENTED OUT)
  // const handleProductToggle = () => {
  //   if (isProductEnabled) {
  //     setProductSelected(!productSelected);
  //   }
  // };

  // // Toggle environment selection - allows selecting both (COMMENTED OUT)
  // const handleEnvironmentToggle = () => {
  //   if (isEnvironmentEnabled) {
  //     setEnvironmentSelected(!environmentSelected);
  //   }
  // };

  /**
   * Prepare export data and headers for CSV export
   * @returns {Promise<{data: Array, headers: Array, filename: String}>}
   */
  const prepareExportData = async () => {
    try {
      // Convert dateSelected to dateFrom and dateTo
      const dateParts = dateSelected.split(" - ");
      let dateFrom = "";
      let dateTo = "";
      if (dateParts.length === 2) { // If dateSelected is a range
        dateFrom = moment(dateParts[0].trim(), "MMM DD, YYYY").format("YYYY-MM-DD");
        dateTo = moment(dateParts[1].trim(), "MMM DD, YYYY").format("YYYY-MM-DD");
      } else { // If dateSelected is a single date
        dateFrom = moment(dateParts[0].trim(), "MMM DD, YYYY").format("YYYY-MM-DD");
        dateTo = dateFrom;
      }

      // CODE FOR EXPORTING ONLY ONE CATEGORY AT A TIME
      let allResults = [];
      let category = "";

      // Export Product data if selected
      if (productSelected) {
        const payload = {
          date_from: dateFrom,
          date_to: dateTo,
          sample_category: "product",
        };

        const { success, message, result } = await exportSampleSubmissions(payload);
        if (success && result && result.length > 0) {
          allResults = result;
          category = "Product";
        } else if (!success) {
          toast.error(message || "Failed to export Product data");
          return { data: [], headers: [], filename: "" };
        } else {
          toast.error("No data to export");
          return { data: [], headers: [], filename: "" };
        }
      } else if (environmentSelected) {
        // Export Environment data if selected
        const payload = {
          date_from: dateFrom,
          date_to: dateTo,
          sample_category: "environment",
        };

        const { success, message, result } = await exportSampleSubmissions(payload);
        if (success && result && result.length > 0) {
          allResults = result;
          category = "Environment";
        } else if (!success) {
          toast.error(message || "Failed to export Environment data");
          return { data: [], headers: [], filename: "" };
        } else {
          toast.error("No data to export");
          return { data: [], headers: [], filename: "" };
        }
      }

      if (allResults.length === 0) {
        toast.error("No data to export");
        return { data: [], headers: [], filename: "" };
      }

      // CODE FOR EXPORTING BOTH PRODUCT AND ENVIRONMENT (COMMENTED OUT)
      // const allResults = [];
      // const categories = [];

      // // If both Product and Environment are selected, make a single API call with blank sample_category
      // if (productSelected && environmentSelected) {
      //   const payload = {
      //   date_from: dateFrom,
      //   date_to: dateTo,
      //     sample_category: "",
      //   };

      //   const { success, message, result } = await exportSampleSubmissions(payload);
      //   if (success && result && result.length > 0) {
      //     allResults.push(...result);
      //     categories.push("Product", "Environment");
      //   } else if (!success) {
      //     toast.error(message || "Failed to export data");
      //   }
      // } else {
      //   // Export Product data if only Product is selected
      //   if (productSelected) {
      //     const payload = {
      //     date_from: dateFrom,
      //     date_to: dateTo,
      //       sample_category: "product",
      //     };

      //     const { success, message, result } = await exportSampleSubmissions(payload);
      //     if (success && result && result.length > 0) {
      //       allResults.push(...result);
      //       categories.push("Product");
      //     } else if (!success) {
      //       toast.error(message || "Failed to export Product data");
      //     }
      //   }

      //   // Export Environment data if only Environment is selected
      //   if (environmentSelected) {
      //     const payload = {
      //     date_from: dateFrom,
      //     date_to: dateTo,
      //       sample_category: "environment",
      //     };

      //     const { success, message, result } = await exportSampleSubmissions(payload);
      //     if (success && result && result.length > 0) {
      //       allResults.push(...result);
      //       categories.push("Environment");
      //     } else if (!success) {
      //       toast.error(message || "Failed to export Environment data");
      //     }
      //   }
      // }

      if (allResults.length === 0) {
        toast.error("No data to export");
        return { data: [], headers: [], filename: "" };
      }

      // Keys that should NOT be included in dynamic headers
      // Spec_flag is per-test and is merged into the test cell via formatExportTestCellValue
      const excludedKeys = new Set(["Sample ID", "Sample Type", "Received Date", "Test", "Result", "Spec_flag"]);

      // Extract all unique dynamic keys from the results (keys that are not fixed or test-related)
      const dynamicKeysSet = new Set();
      allResults.forEach((row) => {
        Object.keys(row).forEach((key) => {
          if (!excludedKeys.has(key)) {
            dynamicKeysSet.add(key);
          }
        });
      });

      // Convert dynamic keys set to array and sort alphabetically
      const dynamicKeys = Array.from(dynamicKeysSet).sort();

      // Create headers for dynamic keys
      const dynamicHeaders = dynamicKeys.map((key) => ({
        label: key,
        key,
      }));

      // CONDITIONAL SAMPLE ID REMOVAL LOGIC START
      // Fixed headers in the specified order (Received Date, Sample ID, Sample Type)
      // const fixedHeaders = [
      //   { label: "Sample Received Date", key: "Received Date" },
      //   { label: "Sample ID", key: "Sample ID" },
      //   { label: "Sample Type", key: "Sample Type" },
      // ];

      // If dynamic headers exist (fields other than fixed headers and test headers),
      // remove "Sample ID" from the export. Otherwise, keep it.
      const hasDynamicHeaders = dynamicHeaders.length > 0;

      // Check if the only dynamic header is "PO"
      const hasOnlyPOAsDynamicHeader = dynamicHeaders.length === 1 && dynamicHeaders[0].key === "PO";

      // Check if other fixed headers (Received Date, Sample Type) have data in any row
      const hasReceivedDateData = allResults.some((row) => row["Received Date"] && row["Received Date"].toString().trim() !== "");
      const hasSampleTypeData = allResults.some((row) => row["Sample Type"] && row["Sample Type"].toString().trim() !== "");
      const hasOtherFixedHeaderData = hasReceivedDateData || hasSampleTypeData;

      // Include Sample ID if:
      // 1. No dynamic headers exist, OR
      // 2. Only Sample ID is the fixed header with data (no Received Date, no Sample Type data)
      //    AND only "PO" is the dynamic header
      const shouldIncludeSampleId = !hasDynamicHeaders || (hasOnlyPOAsDynamicHeader && !hasOtherFixedHeaderData);

      // Fixed headers in the specified order: Received Date, Sample ID, Sample Type
      // Sample ID is conditionally included based on whether dynamic headers exist
      const fixedHeaders = shouldIncludeSampleId
        ? [
          // Keep Sample ID when no dynamic headers OR (only PO as dynamic header AND no other fixed header data)
          { label: "Sample Received Date", key: "Received Date" },
          { label: "Sample ID", key: "Sample ID" },
          { label: "Sample Type", key: "Sample Type" },
        ]
        : [
          // Remove Sample ID when dynamic headers exist (unless only PO with no other fixed header data)
          { label: "Sample Received Date", key: "Received Date" },
          { label: "Sample Type", key: "Sample Type" },
        ];
      // CONDITIONAL SAMPLE ID REMOVAL LOGIC END

      // Extract all unique test names from the results (group by test)
      const testNamesSet = new Set();
      allResults.forEach((row) => {
        if (row.Test && row.Test.trim() !== "") {
          testNamesSet.add(row.Test);
        }
      });

      // Convert test names set to array and sort for consistent column order
      const testNames = Array.from(testNamesSet).sort();

      // Create dynamic headers for each test
      const testHeaders = testNames.map((testName) => ({
        label: testName,
        key: testName,
      }));

      // Combine fixed headers with dynamic key headers and test headers
      // Order: Fixed Headers -> Dynamic Keys (alphabetical) -> Test Headers
      const headers = [...fixedHeaders, ...dynamicHeaders, ...testHeaders];

      // Group data by Sample ID, Sample Type, and Received Date
      // Then pivot test results into columns
      const groupedData = {};
      allResults.forEach((row) => {
        const sampleId = (row["Sample ID"] || "").trim();
        const sampleType = (row["Sample Type"] || "").trim();
        let receivedDate = row["Received Date"] || "";

        // Format received date to MM/DD/YYYY format
        if (receivedDate) {
          // Remove time part if present
          if (receivedDate.includes("T")) {
            receivedDate = receivedDate.split("T")[0];
          }
          // Parse and format to MM/DD/YYYY
          const dateMoment = moment(receivedDate.trim());
          if (dateMoment.isValid()) {
            receivedDate = dateMoment.format("MM/DD/YYYY");
          } else {
            receivedDate = receivedDate.trim();
          }
        } else {
          receivedDate = "";
        }

        // Create a unique key for grouping
        const groupKey = `${sampleId}|${sampleType}|${receivedDate}`;

        if (!groupedData[groupKey]) {
          // CONDITIONAL SAMPLE ID IN DATA START (COMMENTED OUT)
          // Initialize grouped data (Sample ID always included)
          // groupedData[groupKey] = {
          //   "Sample ID": sampleId,
          //   "Sample Type": sampleType,
          //   "Received Date": receivedDate,
          // };

          // Initialize grouped data - conditionally include Sample ID based on shouldIncludeSampleId
          // Order: Received Date, Sample ID, Sample Type
          groupedData[groupKey] = shouldIncludeSampleId
            ? {
              // Include Sample ID when no dynamic headers OR (only PO as dynamic header AND no other fixed header data)
              "Received Date": receivedDate,
              "Sample ID": sampleId,
              "Sample Type": sampleType,
            }
            : {
              // Exclude Sample ID when dynamic headers exist (unless only PO with no other fixed header data)
              "Received Date": receivedDate,
              "Sample Type": sampleType,
            };
          // // CONDITIONAL SAMPLE ID IN DATA END

          // Initialize all dynamic key columns with empty string
          dynamicKeys.forEach((key) => {
            groupedData[groupKey][key] = "";
          });
          // Initialize all test columns with empty string
          testNames.forEach((testName) => {
            groupedData[groupKey][testName] = "";
          });
        }

        // Add dynamic key values from the row
        dynamicKeys.forEach((key) => {
          if (row[key] !== undefined && row[key] !== null && row[key] !== "") {
            groupedData[groupKey][key] = row[key];
          }
        });

        // Add test result (with mapped Spec_flag label when available) to the appropriate column
        if (row.Test) {
          const testCellValue = formatExportTestCellValue(row.Result, row.Spec_flag);
          if (testCellValue !== "") {
            groupedData[groupKey][row.Test] = testCellValue;
          }
        }
      });

      // Convert grouped data object to array
      const normalizedData = Object.values(groupedData);

      // Ensure all rows have all fields (empty string if missing)
      const allHeaderKeys = headers.map((h) => h.key);
      const finalNormalizedData = normalizedData.map((row) => {
        const normalizedRow = {};
        allHeaderKeys.forEach((key) => {
          normalizedRow[key] = row[key] !== undefined && row[key] !== null ? row[key] : "";
        });
        return normalizedRow;
      });

      // Sort by Received Date ascending
      finalNormalizedData.sort((a, b) => {
        const dateA = (a["Received Date"] || "").trim();
        const dateB = (b["Received Date"] || "").trim();

        // Empty dates should come first
        if (!dateA && !dateB) return 0;
        if (!dateA) return -1;
        if (!dateB) return 1;

        // Parse dates for proper comparison (MM/DD/YYYY format)
        const momentA = moment(dateA, "MM/DD/YYYY", true);
        const momentB = moment(dateB, "MM/DD/YYYY", true);

        if (momentA.isValid() && momentB.isValid()) {
          return momentA.valueOf() - momentB.valueOf();
        }

        // Fallback to string comparison if dates are not in expected format
        return dateA.localeCompare(dateB);
      });

      // Generate filename with timestamp and company name
      const timestamp = moment().format("YYYY-MM-DD_HH-mm-ss");
      const user = await getUserCredentials();
      const companyName = user?.company_name ? user.company_name.replace(/\s+/g, "_") : "";

      // CODE FOR EXPORTING ONLY ONE CATEGORY - using single category
      const filename = companyName
        ? `${companyName}_Submissions_Export_${category}_${timestamp}.csv`
        : `Submissions_Export_${category}_${timestamp}.csv`;

      // // CODE FOR EXPORTING BOTH - using categories.join("_") (COMMENTED OUT)
      // const categoryStr = categories.join("_");
      // const filename = companyName
      //   ? `${companyName}_Submissions_Export_${categoryStr}_${timestamp}.csv`
      //   : `Submissions_Export_${categoryStr}_${timestamp}.csv`;

      return { data: finalNormalizedData, headers, filename };
    } catch (error) {
      toast.error("Failed to export data");
      return { data: [], headers: [], filename: "" };
    }
  };

  // Handle export
  const handleExport = async () => {
    if (areResultsLocked) {
      return;
    }

    if (!productSelected && !environmentSelected) {
      return;
    }

    if (!dateSelected || dateSelected === "") {
      toast.error("Please select dates to export data");
      return;
    }

    setLoading(true);
    const { data, headers, filename } = await prepareExportData();

    if (data && data.length > 0 && headers && headers.length > 0) {
      setExportData(data);
      setExportHeaders(headers);
      setCsvFilename(filename);
    }
    setLoading(false);
  };

  /**
   * Trigger CSV download when export data is ready
   */
  useEffect(() => {
    if (exportData.length > 0 && exportHeaders.length > 0 && csvLinkRef.current && csvFilename !== "Submissions_Export.csv") {
      csvLinkRef.current.link.click();
      toast.success("Data exported successfully!");
      // Reset data after download
      setExportData([]);
      setExportHeaders([]);
      setCsvFilename("Submissions_Export.csv");
      onCancel();
    }
  }, [exportData, exportHeaders, csvFilename]); // eslint-disable-line react-hooks/exhaustive-deps

  const canExport = productSelected || environmentSelected;

  // Calculate minDate (3 months ago from today) and maxDate (today)
  const minDate = moment().subtract(3, "months").format("MM/DD/YYYY");
  const maxDate = moment().format("MM/DD/YYYY");

  return (
    <Modal
      open
      closable
      centered
      footer={null}
      onCancel={onCancel}
      width="452px"
      closeIcon={<CloseOutlined className="SampleSubmissionModal__closeIcon" />}
      className="ExportDataAlert"
    >
      <div className="ExportDataTitle">
        {heading}
        <StyledTooltip
          title="Allows you to export submission data for test results available within the last three months."
          placement="top"
        >
          <InfoCircleOutlined
            className="ExportDataTitle__InfoIcon"
            aria-label="About exporting submission data"
          />
        </StyledTooltip>
      </div>
      <div className="ExportDataDatePicker">
        <DatePicker
          value={dateSelected}
          onChange={handleDateChange}
          prevValue={prevDateSelected}
          setPrevValue={setPrevDateSelected}
          searchOnClear
          popoverPlacement="bottom"
          autoAdjustOverflow={false}
          popoverClassName="ExportDataModal__CalendarPopover"
          allowClear
          minDate={minDate}
          maxDate={maxDate}
          datePickerDisplayProps={{
            displayTextPosition: "right",
            displayClassName: "ExportDataModal__DatePicker",
            customCalendarIcon: <CalendarIcon width={16} height={16} />,
          }}
        />
      </div>
      <div className="ExportDataFilterButtons">
        {isProductEnabled && (
          <SubmissionFilterButton
            handleToggleFilter={handleProductToggle}
            specSelected={productSelected}
            specText="Product"
            Icon={ProductIcon}
            hideCloseIcon={isProductEnabled || isEnvironmentEnabled}
          />
        )}
        {isEnvironmentEnabled && (
          <SubmissionFilterButton
            handleToggleFilter={handleEnvironmentToggle}
            specSelected={environmentSelected}
            specText="Environment"
            Icon={EnvironmentIcon}
            hideCloseIcon={isProductEnabled || isEnvironmentEnabled}
          />
        )}
      </div>
      <div className="ExportDataBtn">
        <Button
          type="primary"
          disabled={!canExport}
          onClick={handleExport}
          className="ExportData_SubmitBtn"
          loading={loading}
        >
          Export
        </Button>
      </div>
      {/* Hidden CSVLink - triggered programmatically after data is ready */}
      <CSVLink
        ref={csvLinkRef}
        data={exportData}
        headers={exportHeaders}
        filename={csvFilename}
        className="hidden-csv-link"
      />
    </Modal>
  );
}
